import { useContext, useMemo, useCallback } from 'react'
import GeoContext from 'contexts/geoContext'
import { removeRegionCode } from 'lib/string/regionUtils'
import { setGeoRedirect, Status } from 'storage/geo'
import setRegionCookie from 'cookies/setRegionCookie'
import { useLocation } from 'react-router'

interface SwitchRegionOptions {
  redirectStatus: Status
}

/**
 * @param regionCode of desired region to switch to.
 * Typically the region should always be defined but allows for the case of
 * the loyalty account region possibly being undefined.
 */
function useSwitchRegion(
  regionCode: string | undefined,
  options: SwitchRegionOptions = { redirectStatus: Status.redirected }
): [
  /** Current path with the desired region code */
  switchRegionPath: string | undefined,
  /** Sets the region cookie and geo redirect status */
  handleSwitchRegion: (() => void) | undefined,
] {
  const { currentRegionCode } = useContext(GeoContext)
  const { pathname, search } = useLocation()

  const regionlessUrl = useMemo(() => {
    return `${removeRegionCode(pathname, currentRegionCode)}${search ?? ''}`
  }, [currentRegionCode, pathname, search])

  const switchRegionPath = useMemo(() => {
    if (!regionCode) return
    return `/${regionCode?.toLowerCase()}${regionlessUrl}`
  }, [regionCode, regionlessUrl])

  const handleSwitchRegion = useCallback(() => {
    if (!regionCode) return
    setRegionCookie(regionCode)
    setGeoRedirect(options.redirectStatus)
  }, [regionCode, options.redirectStatus])

  return [
    switchRegionPath,
    (regionCode ? handleSwitchRegion : undefined),
  ]
}

export default useSwitchRegion
